import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import adminService from '../../services/adminService';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { MapPin, Gamepad2, Users, CheckCircle, Plus, Trophy, User, Award } from 'lucide-react';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentGames, setRecentGames] = useState([]);
  const [popularLocations, setPopularLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [statsData, gamesData, locationsData] = await Promise.all([
        adminService.getDashboardStats(),
        adminService.getRecentGames(),
        adminService.getPopularLocations(),
      ]);

      setStats(statsData.stats);
      setRecentGames(gamesData.recent_games);
      setPopularLocations(locationsData.popular_locations);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      alert('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard container">
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard container">
      <div className="dashboard-header">
        <h1>Admin Dashboard</h1>
        <Button onClick={() => navigate('/admin/locations')} variant="primary">
          Manage Locations
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="stats-grid">
        <Card className="stat-card">
          <div className="stat-icon"><MapPin size={20} /></div>
          <div className="stat-content">
            <h3>{stats?.locations?.total || 0}</h3>
            <p>Total Locations</p>
            <small>{stats?.locations?.active || 0} active</small>
          </div>
        </Card>

        <Card className="stat-card">
          <div className="stat-icon"><Gamepad2 size={20} /></div>
          <div className="stat-content">
            <h3>{stats?.games?.total || 0}</h3>
            <p>Total Games</p>
            <small>{stats?.games?.today || 0} today</small>
          </div>
        </Card>

        <Card className="stat-card">
          <div className="stat-icon"><Users size={20} /></div>
          <div className="stat-content">
            <h3>{stats?.users?.total || 0}</h3>
            <p>Total Users</p>
            <small>{stats?.users?.registered || 0} registered</small>
          </div>
        </Card>

        <Card className="stat-card">
          <div className="stat-icon"><CheckCircle size={20} /></div>
          <div className="stat-content">
            <h3>{stats?.locations?.with_drop_points || 0}</h3>
            <p>Ready Locations</p>
            <small>With drop points</small>
          </div>
        </Card>
      </div>

      <div className="dashboard-content">
        {/* Recent Games */}
        <Card title="Recent Games" className="recent-games-card">
          {recentGames.length === 0 ? (
            <p className="no-data">No games played yet</p>
          ) : (
            <div className="games-list">
              {recentGames.map((game, index) => (
                <div key={index} className="game-item">
                  <div className="game-player">
                    <User size={14} /> {game.user_name}
                  </div>
                  <div className="game-score">{game.total_score} pts</div>
                  <div className="game-time">
                    {new Date(game.completed_at).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Popular Locations */}
        <Card title="Popular Locations" className="popular-locations-card">
          {popularLocations.length === 0 ? (
            <p className="no-data">No location data yet</p>
          ) : (
            <div className="locations-list">
              {popularLocations.map((location, index) => (
                <div key={index} className="location-item">
                  <div className="location-rank">#{index + 1}</div>
                  <div className="location-info">
                    <div className="location-name">{location.name}</div>
                    <div className="location-type">{location.type}</div>
                  </div>
                  <div className="location-plays">{location.play_count} plays</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Quick Actions */}
      <Card title="Quick Actions" className="quick-actions-card">
        <div className="action-buttons">
          <Button onClick={() => navigate('/admin/locations/new')} variant="primary">
            <Plus size={16} /> Add New Location
          </Button>
          <Button onClick={() => navigate('/admin/locations')} variant="secondary">
            <MapPin size={16} /> View All Locations
          </Button>
          <Button onClick={() => navigate('/admin/levels')} variant="secondary">
            <Award size={16} /> Manage Level Tiers
          </Button>
          <Button onClick={() => navigate('/leaderboard')} variant="secondary">
            <Trophy size={16} /> View Leaderboard
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default AdminDashboard;
